import { DefineComponent, createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { createPinia } from 'pinia';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRouter, createWebHistory } from 'vue-router';
import * as Sentry from '@sentry/vue';
import AOS from 'aos';
import axios from 'axios';
import qs from 'qs';
import 'vite/modulepreload-polyfill';
import 'core-js/es';
import Components from './components';
import DefaultLayout from './layouts/DefaultLayout.vue';

createInertiaApp({
  progress: {
    delay: 250,
    color: '#FF0000',
    includeCSS: true,
    showSpinner: true,
  },
  title: (title) => title,
  resolve: async (name: string) => {
    const page: DefineComponent = await resolvePageComponent(
      `./pages/${name}.vue`,
      import.meta.glob<DefineComponent>('./pages/**/*.vue')
    );
    const layout = DefaultLayout;

    page.default.layout = typeof page.default.layout === 'object' ? page.default.layout : layout;

    return page;
  },
  setup({ el, App, props, plugin }) {
    const pinia = createPinia();

    const currentApp = createApp({ render: () => h(App, props) });
    currentApp.use(Components);
    currentApp.use(plugin);
    currentApp.use(pinia);
    currentApp.use(AOS.init());

    const router = createRouter({
      history: createWebHistory(),
      routes: [],
    });
    currentApp.use(router);
    const axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_APP_URL,
      paramsSerializer: (params) => {
        return qs.stringify(params);
      },
    });
    currentApp.provide('axios', axiosInstance);

    const sentryDsn = import.meta.env.VITE_SENTRY_DSN || null;

    if (sentryDsn) {
      Sentry.init({
        Vue: currentApp,
        dsn: sentryDsn,
        integrations: [],
        tracesSampleRate: 1.0,
        environment: import.meta.env.MODE,
        ignoreErrors: ['ResizeObserver loop limit exceeded'],
        tracingOptions: {
          trackComponents: true,
        },
        // eslint-disable-next-line spellcheck/spell-checker
        denyUrls: [
          // Google Adsense
          /pagead\/js/i,
          // Facebook flakiness
          /graph\.facebook\.com/i,
          // Facebook blocked
          /connect\.facebook\.net\/en_US\/all\.js/i,
          // Woopra flakiness
          /eatdifferent\.com\.woopra-ns\.com/i,
          /static\.woopra\.com\/js\/woopra\.js/i,
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
          // Other plugins
          /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
          /webappstoolbarba\.texthelp\.com\//i,
          /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
      });
    } else {
      console.error('Sentry DSN is not set');
    }
    currentApp.mount(el);
  },
}).then(() => {
  window.onload = () => {
    try {
      setTimeout(() => {
        afterPageLoadedEvents();
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  };
});

const afterPageLoadedEvents = () => {
  // @ts-ignore
  // eslint-disable-next-line spellcheck/spell-checker
  if (window.instgrm) {
    // @ts-ignore
    // eslint-disable-next-line spellcheck/spell-checker
    window.instgrm.Embeds.process();
  }
};
